import { useEffect, useRef } from 'react';

/**
 * Fires `onReach` once when the document is scrolled past `percent` (0-1).
 *
 * @param {number} percent - 0..1 (e.g. 0.6 for 60%)
 * @param {() => void} onReach
 * @param {object} opts
 * @param {boolean} opts.enabled
 */
export default function useScrollDepth(percent, onReach, opts = {}) {
    const { enabled = true } = opts;
    const firedRef = useRef(false);
    const onReachRef = useRef(onReach);
    onReachRef.current = onReach;

    useEffect(() => {
        if (!enabled) return undefined;
        if (typeof window === 'undefined') return undefined;

        const handle = () => {
            if (firedRef.current) return;
            const doc = document.documentElement;
            const scrolled = window.scrollY || doc.scrollTop || 0;
            const viewport = window.innerHeight || 0;
            const total = (doc.scrollHeight || 0) - viewport;
            if (total <= 0) return;
            const ratio = scrolled / total;
            if (ratio >= percent) {
                firedRef.current = true;
                try { onReachRef.current && onReachRef.current(); } catch (_e) { /* swallow */ }
                window.removeEventListener('scroll', handle);
            }
        };

        window.addEventListener('scroll', handle, { passive: true });
        // Run once in case page already past threshold
        handle();
        return () => window.removeEventListener('scroll', handle);
    }, [enabled, percent]);

    return {
        reset: () => { firedRef.current = false; },
    };
}
